import React, { useCallback, useEffect, useState } from 'react';
import AttachmentsCard from './AttachmentsCard';
import PharmacyCard from './PharmacyCard';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import { getMedicationDisplayName } from './medicationUtils';
import MedicationFormDialog, {
  type MedicationFormMode,
} from './MedicationFormDialog';
import MedicationsCard from './MedicationsCard';
import { formatDateToISO } from '../../utils/dateUtils';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { useToast } from '../../hooks/useToastContext';
import {
  deletePatientMedication,
  getPatientMedications,
  updatePatientMedication,
} from '../../services/api';
import type {
  OrderResponse,
  PatientMedicationListItem,
  PatientMedicationResponse,
} from '../../services/api';
import type { TaskStatus } from '../../services/api/types.gen';
import { EDITABLE_STATUSES } from '../../constants/statuses';
import MarDialogs from './mar/MarDialogs';
import { useMarRecords } from './mar/useMarRecords';

interface PatientMedicationsProps {
  patientId: string;
  /** Episode open in the chart; scopes medication order creation to it. */
  episodeId?: string;
  /** When a visit is selected on the Medications tab, MAR affordances are enabled. */
  visitId?: string;
  /** Status of the selected visit; administrations are only editable on open visits. */
  visitStatus?: TaskStatus;
  /** Visit selector rendered in the card's action row (state lives in the page). */
  visitSelector?: React.ReactNode;
  onMedicationOrderCreated: (order: OrderResponse) => void;
  onDocumentCreated?: () => void;
  /**
   * Called after the medication set changes (add/edit, delete, or discontinue).
   * The parent uses this to refresh its cached medication count so the tab badge
   * stays correct without waiting for a tab switch (ROG-3195). A MAR change is
   * deliberately excluded — it flips `has_administration_records`, not the
   * non-discontinued count the badge shows.
   */
  onMedicationsChanged?: () => void;
}

const PatientMedications: React.FC<PatientMedicationsProps> = ({
  patientId,
  episodeId,
  visitId,
  visitStatus,
  visitSelector,
  onMedicationOrderCreated,
  onDocumentCreated,
  onMedicationsChanged,
}) => {
  const [medications, setMedications] = useState<PatientMedicationListItem[]>(
    []
  );
  // MAR can only be recorded on a selected visit that is still editable.
  const visitEditable =
    Boolean(visitId) && !!visitStatus && EDITABLE_STATUSES.has(visitStatus);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { error: showError } = useToast();

  // Add/edit form dialog control (the form owns its own field state).
  const [formOpen, setFormOpen] = useState(false);
  const [formEditing, setFormEditing] =
    useState<PatientMedicationResponse | null>(null);
  const [formMode, setFormMode] = useState<MedicationFormMode>('standard');

  // Delete confirmation
  const [deletingMedication, setDeletingMedication] =
    useState<PatientMedicationResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchMedications = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getPatientMedications({
        path: { patient_id: patientId },
        query: { include_discontinued: true },
      });
      setMedications(response?.data ?? []);
    } catch (err) {
      console.error('Failed to fetch medications:', err);
      setError('Failed to load medications');
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchMedications();
  }, [fetchMedications]);

  const mar = useMarRecords({
    patientId,
    visitId,
    // A MAR change flips a medication's `has_administration_records` flag.
    onRecordsChanged: fetchMedications,
  });

  const openAddDialog = (mode: MedicationFormMode = 'standard') => {
    setFormEditing(null);
    setFormMode(mode);
    setFormOpen(true);
  };

  const openEditDialog = (med: PatientMedicationResponse) => {
    setFormEditing(med);
    setFormOpen(true);
  };

  const handleDeleteMedication = async () => {
    if (!deletingMedication) return;
    setIsDeleting(true);
    try {
      // The client is configured without `throwOnError`, so HTTP failures come
      // back as a populated `error` rather than a thrown exception — inspect it
      // explicitly and never remove the row optimistically on failure.
      const response = await deletePatientMedication({
        path: { patient_id: patientId, medication_id: deletingMedication.id },
      });
      if (response.error) {
        // 409: the medication has administration records and can only be
        // discontinued. This is an actionable conflict, so tell the user what
        // to do instead of a generic message.
        showError(
          getHttpStatus(response) === 409
            ? 'This medication has administration records and can’t be deleted. Discontinue it instead.'
            : 'Failed to delete medication. Please try again.'
        );
        return;
      }
      setMedications(prev => prev.filter(m => m.id !== deletingMedication.id));
      setDeletingMedication(null);
      onMedicationsChanged?.();
    } catch (err) {
      console.error('Failed to delete medication:', err);
      showError('Failed to delete medication. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleQuickDiscontinue = async (medicationId: string, date: Date) => {
    try {
      // The client is configured without `throwOnError`, so HTTP failures come
      // back as a populated `error` rather than a thrown exception — inspect it
      // explicitly so a failed discontinue isn't silently dropped.
      const response = await updatePatientMedication({
        path: { patient_id: patientId, medication_id: medicationId },
        body: { discontinue_date: formatDateToISO(date) },
      });
      if (response.error) {
        console.error('Failed to discontinue medication:', response.error);
        showError('Failed to discontinue medication. Please try again.');
        return;
      }
      if (response.data) {
        setMedications(prev =>
          prev.map(m => (m.id === medicationId ? response.data! : m))
        );
      }
      // Discontinuing drops the med from the badge's non-discontinued count.
      onMedicationsChanged?.();
    } catch (err) {
      console.error('Failed to discontinue medication:', err);
      showError('Failed to discontinue medication. Please try again.');
    }
  };

  return (
    <>
      <PharmacyCard patientId={patientId} />

      <MedicationsCard
        medications={medications}
        isLoading={isLoading}
        error={error}
        patientId={patientId}
        episodeId={episodeId}
        onRetry={fetchMedications}
        onAddMedication={openAddDialog}
        onEditMedication={openEditDialog}
        onDeleteMedication={setDeletingMedication}
        onDiscontinueMedication={handleQuickDiscontinue}
        onMedicationOrderCreated={onMedicationOrderCreated}
        onDocumentCreated={onDocumentCreated}
        visitId={visitId}
        visitEditable={visitEditable}
        visitSelector={visitSelector}
        marByMedicationId={mar.marByMedicationId}
        onAddMar={mar.actions.onAddRecord}
        onEditMar={mar.actions.onEditRecord}
        onDeleteMar={mar.actions.onDeleteRecord}
      />

      <AttachmentsCard patientId={patientId} />

      <MedicationFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        patientId={patientId}
        editingMedication={formEditing}
        initialMode={formMode}
        onSaved={() => {
          void fetchMedications();
          // Adding a med, or editing one to set/clear a discontinue date, moves
          // the badge count.
          onMedicationsChanged?.();
        }}
      />

      <ConfirmDeleteDialog
        open={deletingMedication !== null}
        onOpenChange={open => !open && setDeletingMedication(null)}
        title='Delete Medication'
        description={
          <>
            Are you sure you want to delete{' '}
            <strong>
              {deletingMedication &&
                getMedicationDisplayName(deletingMedication)}
            </strong>
            ? This action cannot be undone.
          </>
        }
        onConfirm={handleDeleteMedication}
        isDeleting={isDeleting}
      />

      {visitId && (
        <MarDialogs patientId={patientId} visitId={visitId} mar={mar} />
      )}
    </>
  );
};

export default PatientMedications;
